import {useState} from 'react';
import {Check, Copy} from 'lucide-react';
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '../tooltip';
import {cn} from '../lib/cn';
import {IconButton} from '../icon-button';

export type CopyIconProps = {
  textToCopy: string;
  className?: string;
  tooltipText?: string;
  size?: 'sm' | 'md';
  variant?: 'default' | 'outline' | 'ghost';
  onClick?: () => string | Promise<string>;
};

export function CopyIcon({
  textToCopy,
  className,
  tooltipText = 'Click to copy',
  size = 'sm',
  variant = 'outline',
  onClick,
}: CopyIconProps) {
  const [copied, setCopied] = useState(false);

  const iconSize = {
    sm: 16,
    md: 20,
  };

  const buttonSize = {
    sm: 's',
    md: 'default',
  } as const;

  const handleCopy = async () => {
    try {
      const text = onClick ? await onClick() : textToCopy;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            size={buttonSize[size]}
            className={cn(
              'relative disabled:opacity-100',
              {
                '!px-2 !py-0': size === 'sm',
                '!p-3': size === 'md',
                '!h-auto !p-1.5': variant === 'ghost',
              },
              className,
            )}
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy to clipboard'}
          >
            <div
              className={cn(
                'transition-all',
                copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
              )}
            >
              <Check
                className="stroke-[var(--copy-icon-success-color)]"
                size={iconSize[size]}
                strokeWidth={2}
                aria-hidden="true"
              />
            </div>
            <div
              className={cn(
                'absolute transition-all',
                copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100',
              )}
            >
              <Copy size={13} strokeWidth={2} aria-hidden="true" />
            </div>
          </IconButton>
        </TooltipTrigger>
        <TooltipContent className="px-2 py-2 text-xs">{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
